import { useEffect, useState } from "react";
import { useEscKey } from "src/hooks/useEscKey";
import { apiRequest } from "src/services/apiClient";
import { sanitizeInput, trimInput, normalizePlz } from "src/helpers/sanitizeInput";
import MultiPhoneInput from "src/components/PhoneInput/MultiPhoneInput";
import { useTranslation } from "react-i18next";
import { birthDateBounds, isBirthDateValid } from "src/helpers/birthDateBounds";
import { STREET_WITH_NUMBER_REGEX } from "src/helpers/streetAddress";
import { TbUser, TbUsers } from "react-icons/tb";
import type { Patient } from "src/types";
import { normalizeGenderCode } from "src/helpers/gender";
import { ConfirmPopup } from "src/components/Popup/ConfirmPopup";
import { findPersonDuplicates, type DuplicatePatient, type DuplicateRecipient, type PersonDuplicates } from "src/helpers/patientDuplicates";
import "../CreateRecipientModal/createRecipientModal.scss";
import { CityInput } from "src/components/CityInput/CityInput";

type RecipientChoice = "self" | "other";

interface PatientPrefill {
    salutation?: string;
    title?: string;
    firstName?: string;
    lastName?: string;
    birthDate?: string;
    email?: string;
    tel?: string[];
    street?: string;
    plz?: string;
    city?: string;
}

interface Props {
    onClose: () => void;
    onSaved: (patientId?: string) => void;
    /** When set, new patient is linked to this recipient immediately (hides recipient section). */
    recipientId?: string;
    /** When set, edit/delete existing child patient. */
    patient?: Patient | null;
    /** Create mode only: prefill fields (e.g. from the recipient's own data) instead of starting blank. */
    prefill?: PatientPrefill;
    /** Create mode only (requires recipientId): after creating the patient, also call
     *  POST /recipients/:id/same-as-patient so the recipient is marked as this patient too —
     *  used when a recipient (e.g. a parent) forgot to also register herself as a patient. */
    linkAsSelf?: boolean;
    /** Create mode without recipientId: the same person already exists as a patient and the user
     *  picked that one instead of creating a duplicate. */
    onUseExisting?: (patient: DuplicatePatient) => void;
}

export default function CreatePatientModal({ onClose, onSaved, recipientId: presetRecipientId, patient, prefill, linkAsSelf, onUseExisting }: Props) {
    const { t } = useTranslation();
    useEscKey(onClose, true);
    const isEdit = !!patient?.id;

    const SALUTATION_OPTIONS = [
        { value: "", label: "—" },
        { value: "H", label: t("form.herr") },
        { value: "F", label: t("form.frau") },
    ];
    // Divers and "not specified" are one value (helpers/gender.ts). A new patient still starts on "—"
    // so a skipped field doesn't look like a chosen "Divers"; "—" is saved as "D".
    const GENDER_OPTIONS = [
        ...(isEdit ? [] : [{ value: "", label: "—" }]),
        { value: "M", label: t("form.male") },
        { value: "F", label: t("form.female") },
        { value: "D", label: t("form.diverse") },
    ];

    // ── Patient fields ──────────────────────────────────────────────────────────
    const [salutation, setSalutation]   = useState("");
    const [title, setTitle]             = useState("");
    const [firstName, setFirstName]     = useState("");
    const [lastName, setLastName]       = useState("");
    const [gender, setGender]           = useState<string>("");
    const [birthDate, setBirthDate]     = useState("");
    const [email, setEmail]             = useState("");
    const [tel, setTel]                 = useState<string[]>([]);
    const [street, setStreet]           = useState("");
    const [plz, setPlz]                 = useState("");
    const [city, setCity]               = useState("");

    // ── Recipient fields (step 2) ───────────────────────────────────────────────
    const [newRecSalutation, setNewRecSalutation] = useState("H");
    const [newRecFirstName,  setNewRecFirstName]  = useState("");
    const [newRecLastName,   setNewRecLastName]   = useState("");
    const [newRecEmail,      setNewRecEmail]      = useState("");

    // ── Wizard state ────────────────────────────────────────────────────────────
    const [step, setStep]                         = useState<1 | 2>(1);
    const [recipientChoice, setRecipientChoice]   = useState<RecipientChoice>("self");
    const showSteps = !isEdit && !presetRecipientId;

    // ── Async state ─────────────────────────────────────────────────────────────
    const [saving, setSaving]   = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError]     = useState<string | null>(null);
    // Create mode: set once POST /patients succeeded, so a retry after a failed follow-up step
    // (the same-as-patient link) updates that patient rather than creating a duplicate.
    const [createdPatientId, setCreatedPatientId] = useState<string | null>(null);
    // Create mode: the same person (name + birth date) already in the praxis — shown before saving,
    // once per name/birth date entered, so a parent isn't created a second time as her own payer.
    const [duplicates, setDuplicates] = useState<PersonDuplicates | null>(null);
    const [checkedKey, setCheckedKey] = useState<string | null>(null);

    useEffect(() => {
        if (!patient) return;
        setSalutation(patient.salutation ?? "");
        setTitle(patient.title ?? "");
        setFirstName(patient.firstName ?? "");
        setLastName(patient.lastName ?? "");
        setGender(normalizeGenderCode(patient.gender));
        setBirthDate(patient.birthDate ? String(patient.birthDate).slice(0, 10) : "");
        setEmail(patient.email ?? "");
        setTel(patient.tel?.length ? patient.tel : []);
        setStreet(patient.street ?? "");
        setPlz(patient.plz ?? "");
        setCity(patient.city ?? "");
    }, [patient]);

    useEffect(() => {
        if (patient || !prefill) return;
        setSalutation(prefill.salutation ?? "");
        setTitle(prefill.title ?? "");
        setFirstName(prefill.firstName ?? "");
        setLastName(prefill.lastName ?? "");
        setBirthDate(prefill.birthDate ? String(prefill.birthDate).slice(0, 10) : "");
        setEmail(prefill.email ?? "");
        setTel(prefill.tel?.length ? prefill.tel : []);
        setStreet(prefill.street ?? "");
        setPlz(prefill.plz ?? "");
        setCity(prefill.city ?? "");
    }, []);

    const validateStep1 = (): boolean => {
        if (!lastName.trim())               { setError(t("createPatient.lastNameRequired"));  return false; }
        if (!birthDate)                     { setError(t("createPatient.birthDateRequired")); return false; }
        if (!isBirthDateValid(birthDate))   { setError(t("form.birthDateInvalid"));          return false; }
        if (street.trim() && !STREET_WITH_NUMBER_REGEX.test(street.trim())) {
            setError(t("form.streetError")); return false;
        }
        return true;
    };

    /** `recipientId` adds the new patient to that existing recipient instead of the preset/new one;
     *  `linkSelf` overrides linkAsSelf (both chosen from the duplicates panel). */
    const doSave = async (opts: { recipientId?: string; linkSelf?: boolean } = {}) => {
        setSaving(true);
        setError(null);
        try {
            let resolvedRecipientId: string | undefined = opts.recipientId ?? presetRecipientId;

            if (!isEdit && showSteps && !opts.recipientId) {
                if (recipientChoice === "self") {
                    const rec = await apiRequest<{ id: string }>(
                        "/recipients",
                        { method: "POST", body: JSON.stringify({
                            id: crypto.randomUUID(),
                            salutation: salutation || undefined,
                            firstName:  firstName  || undefined,
                            lastName,
                            email:  email  || undefined,
                            tel:    tel.filter(Boolean),
                            street: street || undefined,
                            plz:    plz    || undefined,
                            city:   city   || undefined,
                        }) },
                        { "Content-Type": "application/json" },
                    );
                    resolvedRecipientId = rec.id;
                } else {
                    const rec = await apiRequest<{ id: string }>(
                        "/recipients",
                        { method: "POST", body: JSON.stringify({
                            id: crypto.randomUUID(),
                            salutation: newRecSalutation,
                            firstName:  newRecFirstName || undefined,
                            lastName:   newRecLastName,
                            email:      newRecEmail     || undefined,
                        }) },
                        { "Content-Type": "application/json" },
                    );
                    resolvedRecipientId = rec.id;
                }
            }

            const body = {
                salutation: salutation || undefined,
                title:      title      || undefined,
                firstName:  firstName  || undefined,
                lastName,
                gender: gender || "D",
                birthDate,
                email:  email  || undefined,
                tel:    tel.filter(Boolean),
                street: street || undefined,
                plz:    plz    || undefined,
                city:   city   || undefined,
                ...(resolvedRecipientId && { recipientId: resolvedRecipientId }),
            };

            let savedPatientId: string | undefined;
            const existingId = isEdit ? patient?.id : createdPatientId;
            if (existingId) {
                await apiRequest(
                    `/patients/${existingId}`,
                    { method: "PATCH", body: JSON.stringify(body) },
                    { "Content-Type": "application/json" },
                );
                savedPatientId = existingId;
            } else {
                const newId = crypto.randomUUID();
                await apiRequest(
                    "/patients",
                    { method: "POST", body: JSON.stringify({ id: newId, ...body }) },
                    { "Content-Type": "application/json" },
                );
                savedPatientId = newId;
                // Remembered so a failed link below is retried on the same patient instead of
                // creating another one on every click.
                setCreatedPatientId(newId);
            }

            if (!isEdit && (opts.linkSelf ?? linkAsSelf) && resolvedRecipientId) {
                try {
                    await apiRequest(
                        `/recipients/${resolvedRecipientId}/same-as-patient`,
                        { method: "POST", body: JSON.stringify({ patientId: savedPatientId }) },
                        { "Content-Type": "application/json" },
                    );
                } catch {
                    setError(t("createPatient.linkSelfError"));
                    return;
                }
            }
            onSaved(savedPatientId);
            onClose();
        } catch {
            setError(isEdit || createdPatientId ? t("createPatient.updateError") : t("createPatient.createError"));
        } finally {
            setSaving(false);
        }
    };

    const proceed = () => {
        if (showSteps && recipientChoice === "other") {
            setStep(2);
        } else {
            void doSave();
        }
    };

    // Which matches the panel offers depends on where the modal was opened from.
    const dupRecipientsFor = (d: PersonDuplicates): DuplicateRecipient[] => {
        if (presetRecipientId) {
            // "Als Patient markieren" is prefilled from this very recipient, so it always matches itself.
            if (linkAsSelf) return [];
            // Adding a patient to a recipient who is that person: create her as the recipient herself.
            return d.recipients.filter((r) => r.id === presetRecipientId && !r.sameAsPatientId);
        }
        return recipientChoice === "self" ? d.recipients : [];
    };

    /** True when matches were found and the panel is now shown instead of saving. */
    const checkDuplicates = async (): Promise<boolean> => {
        if (isEdit || createdPatientId) return false;
        const key = `${firstName}|${lastName}|${birthDate}|${recipientChoice}`.toLowerCase();
        if (checkedKey === key) return false;
        setCheckedKey(key);
        try {
            const d = await findPersonDuplicates(firstName, lastName, birthDate);
            if (d.patients.length + dupRecipientsFor(d).length > 0) {
                setDuplicates(d);
                return true;
            }
        } catch {
            // The check only warns — never block creating a patient on it.
        }
        return false;
    };

    const handleStep1Submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!validateStep1()) return;
        setSaving(true);
        const blocked = await checkDuplicates();
        setSaving(false);
        if (!blocked) proceed();
    };

    /** Take the existing patient instead: move it to the preset recipient (with its invoices)
     *  and/or mark it as the recipient herself. */
    const takeExistingPatient = async (p: DuplicatePatient, move: boolean) => {
        setSaving(true);
        setError(null);
        try {
            if (move) {
                await apiRequest(
                    `/patients/${p.id}/change-recipient`,
                    { method: "POST", body: JSON.stringify({ recipientId: presetRecipientId }) },
                    { "Content-Type": "application/json" },
                );
            }
            if (linkAsSelf) {
                await apiRequest(
                    `/recipients/${presetRecipientId}/same-as-patient`,
                    { method: "POST", body: JSON.stringify({ patientId: p.id }) },
                    { "Content-Type": "application/json" },
                );
            }
            onSaved(p.id);
            onClose();
        } catch {
            setError(t("createPatient.dupUseError"));
        } finally {
            setSaving(false);
        }
    };

    const patientDupAction = (p: DuplicatePatient): { label: string; run: () => void } | null => {
        if (presetRecipientId) {
            if (p.recipient?.id === presetRecipientId) {
                return linkAsSelf ? { label: t("createPatient.dupLinkSelf"), run: () => void takeExistingPatient(p, false) } : null;
            }
            return { label: t("createPatient.dupMoveHere"), run: () => void takeExistingPatient(p, true) };
        }
        if (onUseExisting) return { label: t("createPatient.dupUse"), run: () => { onUseExisting(p); onClose(); } };
        return null;
    };

    const recipientDupAction = (r: DuplicateRecipient): { label: string; run: () => void } =>
        presetRecipientId
            ? { label: t("createPatient.dupCreateAsSelf"), run: () => void doSave({ linkSelf: true }) }
            : { label: t("createPatient.dupAddToRecipient"), run: () => void doSave({ recipientId: r.id, linkSelf: !r.sameAsPatientId }) };

    const recipientLabel = (r: DuplicateRecipient) =>
        [r.salutation === "F" ? t("form.frau") : r.salutation === "H" ? t("form.herr") : "", r.title, r.firstName, r.lastName]
            .filter(Boolean).join(" ");

    const handleStep2Submit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!newRecLastName.trim()) { setError(t("createPatient.recipientNewValidation")); return; }
        doSave();
    };

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const handleDelete = async () => {
        setShowDeleteConfirm(false);
        if (!patient?.id) return;
        setDeleting(true);
        setError(null);
        try {
            await apiRequest(`/patients/${patient.id}`, { method: "DELETE" });
            onSaved();
            onClose();
        } catch {
            setError(t("createPatient.deleteError"));
        } finally {
            setDeleting(false);
        }
    };

    const patientName = [firstName, lastName].filter(Boolean).join(" ");

    return (
        <div className="create-modal-overlay" onClick={onClose}>
            <div className="create-modal" onClick={(e) => e.stopPropagation()}>

                {/* ── Step indicator (create mode only) ── */}
                {showSteps && (
                    <div className="create-modal__steps">
                        <div className={`create-modal__step${step === 1 ? " create-modal__step--active" : ""}`}>
                            <span className="create-modal__step-num">1</span>
                            <span className="create-modal__step-label">{t("createPatient.stepPatient")}</span>
                        </div>
                        <div className="create-modal__step-line" />
                        <div className={`create-modal__step${step === 2 ? " create-modal__step--active" : ""}`}>
                            <span className="create-modal__step-num">2</span>
                            <span className="create-modal__step-label">{t("createPatient.stepBilling")}</span>
                        </div>
                    </div>
                )}

                <h2 className="create-modal__title">
                    {isEdit
                        ? t("createPatient.editTitle")
                        : step === 2
                            ? t("createPatient.billingContactTitle")
                            : t("createPatient.title")}
                </h2>

                {/* ══════════════ SAME PERSON ALREADY THERE ══════════════ */}
                {step === 1 && duplicates && (
                    <div className="create-modal__form">
                        <p className="create-modal__dup-intro">
                            {t("createPatient.dupIntro", {
                                name: patientName || lastName,
                                birthDate: birthDate.split("-").reverse().join("."),
                            })}
                        </p>
                        <ul className="create-modal__dup-list">
                            {duplicates.patients.map((p) => {
                                const action = patientDupAction(p);
                                const r = p.recipient;
                                return (
                                    <li key={p.id} className="create-modal__dup-item">
                                        <span className="create-modal__dup-text">
                                            <strong>{t("createPatient.dupPatient", { name: [p.firstName, p.lastName].filter(Boolean).join(" ") })}</strong>
                                            <small>
                                                {!r
                                                    ? t("createPatient.dupNoRecipient")
                                                    : r.id === presetRecipientId
                                                        ? t("createPatient.dupAlreadyHere")
                                                        : r.sameAsPatientId === p.id
                                                            ? t("createPatient.dupPaysSelf", { name: recipientLabel(r) })
                                                            : t("createPatient.dupPaidBy", { name: recipientLabel(r) })}
                                            </small>
                                        </span>
                                        {action && (
                                            <button type="button" className="create-modal__btn create-modal__btn--submit" disabled={saving} onClick={action.run}>
                                                {action.label}
                                            </button>
                                        )}
                                    </li>
                                );
                            })}
                            {dupRecipientsFor(duplicates).map((r) => {
                                const action = recipientDupAction(r);
                                return (
                                    <li key={r.id} className="create-modal__dup-item">
                                        <span className="create-modal__dup-text">
                                            <strong>{t("createPatient.dupRecipient", { name: recipientLabel(r) })}</strong>
                                            <small>{t("createPatient.dupRecipientHint")}</small>
                                        </span>
                                        <button type="button" className="create-modal__btn create-modal__btn--submit" disabled={saving} onClick={action.run}>
                                            {action.label}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                        {presetRecipientId && duplicates.patients.some((p) => p.recipient?.id !== presetRecipientId) && (
                            <p className="create-modal__dup-hint">{t("createPatient.dupMoveHint")}</p>
                        )}

                        {error && <p className="create-modal__error">{error}</p>}

                        <div className="create-modal__actions">
                            <button
                                type="button"
                                className="create-modal__btn create-modal__btn--cancel create-modal__btn--back"
                                disabled={saving}
                                onClick={() => { setDuplicates(null); setCheckedKey(null); setError(null); }}
                            >
                                ← {t("createPatient.back")}
                            </button>
                            <button
                                type="button"
                                className="create-modal__btn create-modal__btn--cancel"
                                disabled={saving}
                                onClick={() => { setDuplicates(null); setError(null); proceed(); }}
                            >
                                {saving ? t("createPatient.creating") : t("createPatient.dupCreateAnyway")}
                            </button>
                        </div>
                    </div>
                )}

                {/* ══════════════ STEP 1 ══════════════ */}
                {step === 1 && !duplicates && (
                    <form className="create-modal__form" onSubmit={handleStep1Submit}>
                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label htmlFor="cp-salutation">{t("form.salutation")}</label>
                                <select id="cp-salutation" autoFocus value={salutation} onChange={(e) => setSalutation(e.target.value)}>
                                    {SALUTATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div className="create-modal__field">
                                <label htmlFor="cp-title">{t("form.title")}</label>
                                <input id="cp-title" value={title} maxLength={20} onChange={(e) => setTitle(sanitizeInput(e.target.value))} onBlur={(e) => setTitle(trimInput(e.target.value))} placeholder={t("form.drPlaceholder")} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label htmlFor="cp-firstname">{t("form.firstName")}</label>
                                <input id="cp-firstname" value={firstName} maxLength={50} onChange={(e) => setFirstName(sanitizeInput(e.target.value))} onBlur={(e) => setFirstName(trimInput(e.target.value))} />
                            </div>
                            <div className="create-modal__field">
                                <label htmlFor="cp-lastname">{t("form.lastName")} *</label>
                                <input id="cp-lastname" value={lastName} maxLength={50} onChange={(e) => setLastName(sanitizeInput(e.target.value))} onBlur={(e) => setLastName(trimInput(e.target.value))} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label htmlFor="cp-gender">{t("form.gender")}</label>
                                <select id="cp-gender" value={gender} onChange={(e) => setGender(e.target.value)}>
                                    {GENDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div className="create-modal__field">
                                <label htmlFor="cp-birthdate">{t("form.birthDate")} *</label>
                                <input id="cp-birthdate" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} {...birthDateBounds()} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label htmlFor="cp-email">{t("form.email")}</label>
                                <input id="cp-email" type="email" value={email} onChange={(e) => setEmail(sanitizeInput(e.target.value))} onBlur={(e) => setEmail(trimInput(e.target.value))} />
                            </div>
                            <div className="create-modal__field">
                                <label>{t("form.phone")}</label>
                                <MultiPhoneInput value={tel} onChange={setTel} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label htmlFor="cp-street">{t("form.streetAndNumber")}</label>
                                <input id="cp-street" value={street} onChange={(e) => setStreet(sanitizeInput(e.target.value))} onBlur={(e) => setStreet(trimInput(e.target.value))} placeholder="Hauptstraße 22a" />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field create-modal__field--short">
                                <label htmlFor="cp-plz">{t("form.plz")}</label>
                                <input id="cp-plz" value={plz} onChange={(e) => setPlz(normalizePlz(e.target.value))} />
                            </div>
                            <div className="create-modal__field">
                                <label htmlFor="cp-city">{t("form.city")}</label>
                                <CityInput id="cp-city" plz={plz} value={city} onChange={(v) => setCity(sanitizeInput(v))} onBlur={(v) => setCity(trimInput(v))} />
                            </div>
                        </div>

                        {/* ── Billing question (new patient only) ── */}
                        {showSteps && (
                            <>
                                <div className="create-modal__section-divider">
                                    <span>{t("createPatient.billingQuestion")}</span>
                                </div>
                                <div className="create-modal__billing-options">
                                    <button
                                        type="button"
                                        className={`create-modal__billing-opt${recipientChoice === "self" ? " create-modal__billing-opt--active" : ""}`}
                                        onClick={() => setRecipientChoice("self")}
                                    >
                                        <span className="create-modal__billing-opt-icon"><TbUser /></span>
                                        <span className="create-modal__billing-opt-text">
                                            <strong>{t("createPatient.recipientOptionSelf")}</strong>
                                            <small>{t("createPatient.recipientOptionSelfDesc")}</small>
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`create-modal__billing-opt${recipientChoice === "other" ? " create-modal__billing-opt--active" : ""}`}
                                        onClick={() => setRecipientChoice("other")}
                                    >
                                        <span className="create-modal__billing-opt-icon"><TbUsers /></span>
                                        <span className="create-modal__billing-opt-text">
                                            <strong>{t("createPatient.recipientOptionOther")}</strong>
                                            <small>{t("createPatient.recipientOptionOtherDesc")}</small>
                                        </span>
                                    </button>
                                </div>
                            </>
                        )}

                        {error && <p className="create-modal__error">{error}</p>}

                        <div className="create-modal__actions">
                            {isEdit && (
                                <button
                                    type="button"
                                    className="create-modal__btn create-modal__btn--danger"
                                    disabled={deleting || saving}
                                    onClick={() => setShowDeleteConfirm(true)}
                                >
                                    {deleting ? "…" : t("form.delete")}
                                </button>
                            )}
                            {showDeleteConfirm && (
                                <ConfirmPopup
                                    message={t("createPatient.deleteConfirm")}
                                    onConfirm={handleDelete}
                                    onCancel={() => setShowDeleteConfirm(false)}
                                />
                            )}
                            <button type="button" className="create-modal__btn create-modal__btn--cancel" onClick={onClose}>
                                {t("form.cancel")}
                            </button>
                            <button type="submit" className="create-modal__btn create-modal__btn--submit" disabled={saving || deleting}>
                                {saving
                                    ? t("createPatient.creating")
                                    : isEdit
                                        ? t("form.save")
                                        : showSteps && recipientChoice === "other"
                                            ? t("createPatient.next")
                                            : t("createPatient.create")}
                            </button>
                        </div>
                    </form>
                )}

                {/* ══════════════ STEP 2 ══════════════ */}
                {step === 2 && (
                    <form className="create-modal__form" onSubmit={handleStep2Submit}>
                        {patientName && (
                            <p className="create-modal__step-context">
                                {t("createPatient.patientContext", { name: patientName })}
                            </p>
                        )}

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label>{t("form.salutation")}</label>
                                <select autoFocus value={newRecSalutation} onChange={(e) => setNewRecSalutation(e.target.value)}>
                                    <option value="H">{t("form.herr")}</option>
                                    <option value="F">{t("form.frau")}</option>
                                </select>
                            </div>
                            <div className="create-modal__field">
                                <label>{t("form.firstName")}</label>
                                <input value={newRecFirstName} maxLength={50} onChange={(e) => setNewRecFirstName(sanitizeInput(e.target.value))} onBlur={(e) => setNewRecFirstName(trimInput(e.target.value))} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label>{t("form.lastName")} *</label>
                                <input value={newRecLastName} maxLength={50} onChange={(e) => setNewRecLastName(sanitizeInput(e.target.value))} onBlur={(e) => setNewRecLastName(trimInput(e.target.value))} />
                            </div>
                            <div className="create-modal__field">
                                <label>{t("form.email")}</label>
                                <input type="email" value={newRecEmail} onChange={(e) => setNewRecEmail(sanitizeInput(e.target.value))} onBlur={(e) => setNewRecEmail(trimInput(e.target.value))} />
                            </div>
                        </div>

                        {error && <p className="create-modal__error">{error}</p>}

                        <div className="create-modal__actions">
                            <button
                                type="button"
                                className="create-modal__btn create-modal__btn--cancel create-modal__btn--back"
                                onClick={() => { setStep(1); setError(null); }}
                            >
                                ← {t("createPatient.back")}
                            </button>
                            <button type="submit" className="create-modal__btn create-modal__btn--submit" disabled={saving}>
                                {saving ? t("createPatient.creating") : t("createPatient.create")}
                            </button>
                        </div>
                    </form>
                )}

            </div>
        </div>
    );
}
